import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const defaultTranslations = {
  "app.title": { nl: "Technisch Portaal", de: "Technisches Portal", en: "Technical Portal" },
  "nav.dashboard": { nl: "Dashboard", de: "Dashboard", en: "Dashboard" },
  "nav.documents": { nl: "Documenten", de: "Dokumente", en: "Documents" },
  "nav.users": { nl: "Gebruikers", de: "Benutzer", en: "Users" },
  "nav.settings": { nl: "Instellingen", de: "Einstellungen", en: "Settings" },
  "nav.logout": { nl: "Uitloggen", de: "Abmelden", en: "Logout" },
  "auth.login": { nl: "Inloggen", de: "Anmelden", en: "Login" },
  "auth.email": { nl: "E-mail", de: "E-Mail", en: "Email" },
  "auth.password": { nl: "Wachtwoord", de: "Passwort", en: "Password" },
  "auth.welcome": { nl: "Welkom terug", de: "Willkommen zurück", en: "Welcome back" },
  "docs.upload": { nl: "Document uploaden", de: "Dokument hochladen", en: "Upload document" },
  "docs.download": { nl: "Downloaden", de: "Herunterladen", en: "Download" },
  "docs.preview": { nl: "Bekijken", de: "Ansehen", en: "Preview" },
  "docs.search": { nl: "Zoeken...", de: "Suchen...", en: "Search..." },
  "docs.recent": { nl: "Recent geopend", de: "Kürzlich geöffnet", en: "Recently opened" },
  "docs.all": { nl: "Alle documenten", de: "Alle Dokumente", en: "All documents" },
  "docs.name": { nl: "Naam", de: "Name", en: "Name" },
  "docs.category": { nl: "Categorie", de: "Kategorie", en: "Category" },
  "docs.description": { nl: "Beschrijving", de: "Beschreibung", en: "Description" },
  "docs.file": { nl: "Bestand", de: "Datei", en: "File" },
  "users.add": { nl: "Gebruiker toevoegen", de: "Benutzer hinzufügen", en: "Add user" },
  "users.name": { nl: "Naam", de: "Name", en: "Name" },
  "users.role": { nl: "Rol", de: "Rolle", en: "Role" },
  "users.active": { nl: "Actief", de: "Aktiv", en: "Active" },
  "settings.translations": { nl: "Vertalingen", de: "Übersetzungen", en: "Translations" },
  "settings.categories": { nl: "Categorieën", de: "Kategorien", en: "Categories" },
  "settings.general": { nl: "Algemeen", de: "Allgemein", en: "General" },
  "common.save": { nl: "Opslaan", de: "Speichern", en: "Save" },
  "common.cancel": { nl: "Annuleren", de: "Abbrechen", en: "Cancel" },
  "common.delete": { nl: "Verwijderen", de: "Löschen", en: "Delete" },
  "common.edit": { nl: "Bewerken", de: "Bearbeiten", en: "Edit" },
  "common.add": { nl: "Toevoegen", de: "Hinzufügen", en: "Add" },
  "stats.documents": { nl: "Documenten", de: "Dokumente", en: "Documents" },
  "stats.users": { nl: "Gebruikers", de: "Benutzer", en: "Users" },
  "stats.downloads": { nl: "Downloads", de: "Downloads", en: "Downloads" },
  "role.superadmin": { nl: "Superadmin", de: "Superadmin", en: "Superadmin" },
  "role.admin": { nl: "Admin", de: "Admin", en: "Admin" },
  "role.monteur": { nl: "Monteur", de: "Monteur", en: "Technician" },
};

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('carer-language') || 'nl';
  });
  const [translations, setTranslations] = useState(defaultTranslations);

  useEffect(() => {
    localStorage.setItem('carer-language', language);
  }, [language]);

  useEffect(() => {
    const fetchTranslations = async () => {
      try {
        const res = await axios.get(`${API}/translations`);
        if (res.data && Object.keys(res.data).length > 0) {
          setTranslations({ ...defaultTranslations, ...res.data });
        }
      } catch (e) {
        console.log('Using default translations');
      }
    };
    fetchTranslations();
  }, []);

  const t = (key) => {
    const translation = translations[key];
    if (!translation) return key;
    return translation[language] || translation.nl || key;
  };

  const cycleLanguage = () => {
    setLanguage(prev => {
      if (prev === 'nl') return 'de';
      if (prev === 'de') return 'en';
      return 'nl';
    });
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage: cycleLanguage, t, translations, setTranslations }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
